import { FC, useState } from 'react'
import { PixelCrop } from 'react-image-crop'
import { useDispatch } from 'react-redux'

import { Button } from 'ui'
import { UPLOAD_ATTACHMENTS_TYPES } from 'common'

import useReadImage from 'components/ImageCrop/hooks/useReadImage'
import ImageCrop from 'components/ImageCrop'
import BaseModal from 'components/Modals/BaseModal'
import FileUpload from 'components/Forms/FileUpload'
import { getCroppedImg } from 'components/ImageCrop/helpers'

import { useUpload, useApi } from 'components/Hooks'
import { triggerResourcesRefresh } from 'store/actions/resources'
import { setUserInfo } from 'store/actions/users'
import { closeModal } from 'store/actions/modals'

import { t } from 'translations'

const ProfilePictureUploadModal: FC<{ callback?: () => Promise<void>; userId?: string }> = ({ callback, userId }) => {
  const dispatch = useDispatch()
  const { handleApi } = useApi({ method: 'POST', url: 'users/picture' })
  const [file, setFile] = useState<File | null>(null)
  const [cropDimensions, setCropDimensions] = useState<PixelCrop | null>(null)
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null)

  const fileSource = useReadImage(file)

  const { uploadHandler } = useUpload({ attachmentType: UPLOAD_ATTACHMENTS_TYPES.PROFILE })

  const handleChange = (file?: File) => {
    setFile(file ?? null)
  }

  const handleCancel = () => {
    dispatch(closeModal())
    setFile(null)
  }

  const onCropComplete = (croppedArea: PixelCrop) => {
    setCropDimensions(croppedArea)
  }

  const reloadUserInfo = () => {
    triggerResourcesRefresh(dispatch, { resource: ['users'] })
    dispatch(setUserInfo())
  }

  const cropAndUpload = async () => {
    if (!cropDimensions || !file || !imageDimensions) return

    const result = await getCroppedImg(file, cropDimensions, imageDimensions)

    if (!result) return

    const key = await uploadHandler({ fileName: file.name, file: result, contentType: file.type })
    const payload = {
      attachmentType: UPLOAD_ATTACHMENTS_TYPES.PROFILE,
      fileName: file.name,
      key,
      userId
    }
    await handleApi(payload)
    reloadUserInfo()
    if (callback) await callback()
    dispatch(closeModal())
  }

  return (
    <BaseModal hideButtons title="employee.myProfile.PROFILEPICTUREUPLOAD_MODAL">
      <div className="text-center">
        {fileSource ? <ImageCrop image={fileSource} onCropComplete={onCropComplete} setImageDimensions={setImageDimensions} /> : null}
      </div>
      <FileUpload onChange={handleChange} validExtensions={['png', 'jpg', 'jpeg']} />
      <div className="d-flex flex-column justify-content-center align-items-center">
        <div>
          <Button className="mr-2" color="primary" disabled={!file} data-cy="crop-and-upload" onClick={cropAndUpload}>
            {t('employee.myProfile.CROP_AND_UPLOAD')}
          </Button>
          <Button color="secondary" outlined onClick={handleCancel} data-cy="cancel-edit-picture">
            {t('CANCEL')}
          </Button>
        </div>
      </div>
    </BaseModal>
  )
}

export default ProfilePictureUploadModal
